<template>
  <div class="content-wrapper view-doctor-manage p-0">
    <div class="content">
      <div class="card">
        <div class="card-body">
          <h4><i class="fas fa-user-md"></i> จัดการแพทย์</h4>

          <!-- ภาพรวมตารางเดือน: หมอแต่ละคนลงวันไหน -->
          <div class="doctor-overview mb-3">
            <div class="doctor-overview-head">
              <strong>ภาพรวมตารางแพทย์</strong>
              <div class="doctor-overview-nav">
                <b-button size="sm" variant="light" @click="stepOverviewMonth(-1)">‹</b-button>
                <span class="doctor-overview-month">{{ overviewMonthLabel }}</span>
                <b-button size="sm" variant="light" @click="stepOverviewMonth(1)">›</b-button>
              </div>
            </div>
            <DoctorMonthOverview
              :year="overviewYear"
              :month="overviewMonth"
              :members="scheduleMembers"
              :doctors="overviewDoctors"
              :highlight-doctor-id="highlightDoctorId"
              :loading="scheduleLoading"
              @day-click="onOverviewDayClick"
              @doctor-click="onOverviewDoctorClick"
            />
            <div
              v-if="highlightDate || highlightDoctorId !== null"
              class="doctor-overview-banner"
            >
              <span v-if="highlightDate">
                🟢 ลงตรวจ {{ highlightDate }} ({{ highlightedDoctorIds.length }} คน) — แสดงด้านบนของรายชื่อ
              </span>
              <span v-else>
                🔵 {{ highlightDoctorName }} — เน้นวันที่ลงตรวจในปฏิทิน
              </span>
              <b-button
                size="sm"
                variant="link"
                class="doctor-overview-clear p-0 ml-2"
                v-b-tooltip.hover
                title="ล้างไฮไลต์"
                @click="clearOverviewHighlight"
              >
                <i class="fas fa-eraser"></i>
              </b-button>
            </div>
          </div>

          <b-table
            :style="{ minHeight: '300px' }"
            outlined
            noCollapse
            sort-icon-left
            :sort-by="highlightDate ? '' : 'creationDt'"
            :sort-desc="false"
            head-variant="light"
            :items="orderedDoctorMembers"
            :fields="doctorTableField"
            :tbody-tr-class="doctorRowClass"
            hover
            responsive
            @row-clicked="onDoctorRowClicked"
          >
            <template #cell(index)="data">{{ data.index + 1 }}.</template>
            <template #cell(fullName)="data">
              {{ data.value }}
              <span
                v-if="data.item.user && data.item.user.email == userInfo.email"
                class="bg-secondary rounded p-1 mx-1"
                >คุณ</span
              >
            </template>
            <template v-slot:cell(doctorCode)="data">
              <b-input-group v-if="isManager" size="sm">
                <b-form-input
                  v-model="data.item.doctorCode"
                  trim
                  placeholder="-"
                  :disabled="editingDoctorCodeUid !== data.item.uid"
                  style="max-width: 10ch"
                ></b-form-input>
                <b-input-group-append>
                  <template v-if="editingDoctorCodeUid === data.item.uid">
                    <b-button variant="outline-success" @click="confirmUpdateDoctorCode(data.item)">
                      <i class="fas fa-check"></i>
                    </b-button>
                    <b-button variant="outline-danger" @click="cancelEditDoctorCode">
                      <i class="fas fa-times"></i>
                    </b-button>
                  </template>
                  <b-button v-else variant="outline-secondary" @click="startEditDoctorCode(data.item)">
                    <i class="fas fa-edit"></i>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <span v-else>{{ data.item.doctorCode || "-" }}</span>
            </template>
            <template v-slot:cell(drApptColor)="data">
              <VSwatches
                v-if="isUserDoctor(data.item.authIdItems)"
                v-model="data.item.drApptColor"
                :swatches="doctorColorListCode"
                row-length="5"
                swatch-size="31"
                :trigger-style="{ width: '31px', height: '31px' }"
                @input="updateMember(data.item)"
              ></VSwatches>
            </template>
            <template v-slot:cell(defaultRoomId)="data">
              <b-form-select
                v-if="isManager"
                v-model="data.item.defaultRoomId"
                :options="getRoomList"
                value-field="id"
                text-field="title"
                size="sm"
                @change="updateMember(data.item)"
              >
                <template #first>
                  <b-form-select-option :value="null"
                    >- ไม่ระบุ -</b-form-select-option
                  >
                </template>
              </b-form-select>
              <span v-else>{{ data.item.defaultRoom?.name || "-" }}</span>
            </template>
            <template v-slot:cell(button)="data">
              <b-button
                size="sm"
                :variant="
                  data.item.doctorWorkingHours.length > 0
                    ? 'outline-info'
                    : 'outline-warning'
                "
                @click="openWorkingHoursModal(data.item)"
              >
                <i class="fas fa-clock"></i>
              </b-button>
            </template>
          </b-table>
        </div>
      </div>
    </div>
    <WorkingHoursModal
      ref="workingHoursModal"
      @closed="onWorkingHoursModalClosed"
    />
    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import Dialog from "@/components/modal/Dialog";
import WorkingHoursModal from "@/components/modal/WorkingHoursModal.vue";
import DoctorMonthOverview from "@/components/workingHours/DoctorMonthOverview.vue";
import Loading from "@/components/Loading";
import moment from "moment";
import { doctorIdsOnDate } from "@/utils/doctorMonthOverview";

import VSwatches from "vue-swatches";
import "vue-swatches/dist/vue-swatches.css";
import "@/theme/DoctorManage.scss";
import { eventBus } from "@/main";

const TH_MONTHS = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
];

export default {
  name: "DoctorManage",
  components: {
    Dialog,
    Loading,
    VSwatches,
    WorkingHoursModal,
    DoctorMonthOverview,
  },
  data() {
    const now = moment();
    return {
      isLoading: false,
      branchMembers: [],
      // month-overview state
      overviewYear: now.year(),
      overviewMonth: now.month() + 1,
      scheduleMembers: [],
      scheduleLoading: false,
      scheduleLoadedKey: null, // "YYYY-M" of the last successful load
      highlightDoctorId: null, // clicked doctor row -> highlight their days
      highlightDate: null, // clicked day -> reorder list to that day's doctors
      doctorTableField: [
        { key: "index", label: "" },
        { key: "fullName", label: "ชื่อ", sortable: true, class: "narrow-spacing" },
        { key: "doctorCode", label: "รหัสแพทย์", sortable: true, class: "narrow-spacing" },
        { key: "user.email", label: "อีเมล", sortable: true, class: "narrow-spacing" },
        { key: "drApptColor", label: "สี", class: "text-center  align-middle" },
        { key: "defaultRoomId", label: "ห้องตรวจเริ่มต้น", class: "text-center  align-middle" },
        { key: "button", label: "วันเวลาทำงาน", class: "text-center  align-middle" },
      ],
      editingDoctorCodeUid: null,
    };
  },
  created() {
    this.init();
  },
  computed: {
    ...mapGetters({
      doctorColorList: "moduleMaster/getDoctorColorList",
      userInfo: "moduleUser/getUserInfo",
      isManager: "moduleUser/isManager",
      getRoomList: "moduleUser/getRoomList",
    }),
    doctorColorListCode() {
      return this.doctorColorList.map(item => item.code);
    },
    doctorMembers() {
      return this.branchMembers.filter(i => i.authIdItems?.includes(2));
    },
    // Normalized doctors for DoctorMonthOverview, keyed by branchMemberId (= id).
    overviewDoctors() {
      return this.doctorMembers.map(m => ({
        branchMemberId: m.id,
        name: m.fullName,
        color: m.drApptColor,
      }));
    },
    overviewMonthLabel() {
      return `${TH_MONTHS[this.overviewMonth - 1]} ${this.overviewYear + 543}`;
    },
    highlightDoctorName() {
      if (this.highlightDoctorId == null) return "";
      const m = this.doctorMembers.find(d => d.id === this.highlightDoctorId);
      return m ? m.fullName : "";
    },
    highlightedDoctorIds() {
      if (!this.highlightDate) return [];
      return doctorIdsOnDate(this.scheduleMembers, this.highlightDate);
    },
    orderedDoctorMembers() {
      if (!this.highlightDate) return this.doctorMembers;
      const working = new Set(this.highlightedDoctorIds);
      return [...this.doctorMembers].sort(
        (a, b) => (working.has(b.id) ? 1 : 0) - (working.has(a.id) ? 1 : 0)
      );
    },
  },
  methods: {
    ...mapActions({
      fetchBranchMember: "moduleBranch/fetchBranchMember",
      fetchBranchSchedule: "moduleWorkingHours/fetchBranchSchedule",
      updateBranchMember: "moduleBranch/updateBranchMember",
    }),
    async init() {
      await this.getMember();
      this.loadBranchSchedule();
    },
    async getMember() {
      this.isLoading = true;
      this.editingDoctorCodeUid = null;
      this.fetchBranchMember({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      })
        .then(res => {
          if (res.status == "200") {
            this.branchMembers = res.data;
            this.branchMembers.forEach(member => {
              member.fullName = member.user.fullName;
            });
          } else {
            this.branchMembers = [];
          }
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    startEditDoctorCode(member) {
      this.editingDoctorCodeUid = member.uid;
    },
    cancelEditDoctorCode() {
      this.editingDoctorCodeUid = null;
      this.getMember();
    },
    confirmUpdateDoctorCode(branchMember) {
      this.$refs.Dialog.showAlertConfirm(
        "แก้ไขรหัสแพทย์ ?",
        `คุณต้องการแก้ไขรหัสแพทย์ของ "${branchMember.fullName}" เป็น "${branchMember.doctorCode || '-'}"`
      ).then(result => {
        this.editingDoctorCodeUid = null;
        if (result.isConfirmed) {
          this.updateMember(branchMember);
        } else {
          this.getMember();
        }
      });
    },
    updateMember(branchMember) {
      if (branchMember) {
        let data = {
          uid: branchMember.uid,
          authIdItems: branchMember.authIdItems,
          drApptColor: branchMember.drApptColor,
          doctorCode: branchMember.doctorCode,
          defaultRoomId: branchMember.defaultRoomId ?? null,
        };
        this.isLoading = true;
        this.updateBranchMember({
          member: data,
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
        })
          .then(res => {
            if (res.status == "200") {
              this.getMember();
            }
            this.isLoading = false;
          })
          .catch(err => {
            if (
              err.response?.status === 400 &&
              typeof err.response?.data === "string" &&
              err.response.data.includes("doctor code already exists")
            ) {
              eventBus.$emit("alertToast", {
                message: "รหัสแพทย์นี้มีอยู่ในคลินิกแล้ว",
                variant: "danger",
              });
            }
            this.getMember();
            this.isLoading = false;
          });
      }
    },
    isUserDoctor(arr) {
      return arr.find(item => item === 2);
    },
    openWorkingHoursModal({ id }) {
      this.$refs.workingHoursModal.show({ branchMemberId: id });
    },
    // After editing in the modal, the doctor's hours may have changed — refresh
    // the member list and the month-overview so dots/rules stay in sync.
    onWorkingHoursModalClosed() {
      this.getMember();
      if (this.scheduleLoadedKey) this.loadBranchSchedule();
    },
    async loadBranchSchedule() {
      const key = `${this.overviewYear}-${this.overviewMonth}`;
      const start = moment(
        `${this.overviewYear}-${this.overviewMonth}-01`,
        "YYYY-M-DD"
      );
      this.scheduleLoading = true;
      try {
        const members = await this.fetchBranchSchedule({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          from: start.format("YYYY-MM-DD"),
          to: start.clone().endOf("month").format("YYYY-MM-DD"),
        });
        // Ignore a stale response if the user paged months meanwhile.
        if (`${this.overviewYear}-${this.overviewMonth}` !== key) return;
        this.scheduleMembers = members;
        this.scheduleLoadedKey = key;
      } finally {
        this.scheduleLoading = false;
      }
    },
    stepOverviewMonth(delta) {
      const next = moment(
        `${this.overviewYear}-${this.overviewMonth}-01`,
        "YYYY-M-DD"
      ).add(delta, "month");
      this.overviewYear = next.year();
      this.overviewMonth = next.month() + 1;
      this.highlightDate = null;
      this.loadBranchSchedule();
    },
    onOverviewDayClick(dateStr) {
      this.highlightDate = this.highlightDate === dateStr ? null : dateStr;
      this.highlightDoctorId = null;
    },
    onOverviewDoctorClick(branchMemberId) {
      this.highlightDoctorId =
        this.highlightDoctorId === branchMemberId ? null : branchMemberId;
      this.highlightDate = null;
    },
    onDoctorRowClicked(item) {
      this.highlightDoctorId =
        this.highlightDoctorId === item.id ? null : item.id;
      this.highlightDate = null;
    },
    clearOverviewHighlight() {
      this.highlightDoctorId = null;
      this.highlightDate = null;
    },
    doctorRowClass(item) {
      if (!item) return "";
      if (this.highlightDoctorId !== null && item.id === this.highlightDoctorId)
        return "doctor-row-selected";
      if (this.highlightDate && this.highlightedDoctorIds.includes(item.id))
        return "doctor-row-match";
      return "";
    },
  },
  destroyed() {
    // This page edits doctor codes/colors/rooms — refresh the global cache on leave.
    this.$store.dispatch("moduleUser/fetchAllBranchMembers", {
      clinicUrl: this.$route.params.clinicUrl,
      branchUrl: this.$route.params.branchUrl,
    });
  },
};
</script>

<style>
.doctor-overview-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.doctor-overview-nav {
  display: flex;
  align-items: center;
  gap: 6px;
}
.doctor-overview-month {
  font-weight: 600;
  font-size: 14px;
  min-width: 130px;
  text-align: center;
}
.doctor-overview-banner {
  margin-top: 10px;
  padding: 6px 10px;
  background: #f2fbf6;
  color: #1a8a4a;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.doctor-overview-clear {
  color: #1a8a4a;
  opacity: 0.7;
}
.doctor-overview-clear:hover {
  opacity: 1;
}
.doctor-row-selected > td {
  background-color: #eaf1ff !important;
}
.doctor-row-match > td {
  background-color: #f2fbf6 !important;
}
</style>
